import React, { useEffect, useMemo } from 'react';
import Script from 'next/script';
import { isPharmaprixBanner } from '@/utils';
import { banners } from '@/data/constants';
import Cookies from 'js-cookie';

const SalesfloorWidgetComponent: React.FC = () => {
  useEffect(() => {
    window.__is_new_widget = true;

    // If the widget was already rendered before, but is hidden, we can un-hide the widget OR footer depending on the
    // "state" of salesfloor. `reEvaluateWidgetRules` will automatically determine this
    window.sf_widget?.widgets.base.reEvaluateWidgetRules();

    return () => {
      // Upon unmounting the component, hide the widgets
      // (note this *may* become a problem in the future when switching between beauty pages)
      for (const widget of ['#sf-widget-companion', '#sf-footer-companion'] as const) {
        window.sf_widget?.utils.hideWebsiteNodes(widget);
      }
    };
  }, []);

  // Salesfloor accessibility "fix"
  useEffect(() => {
    const checkSalesfloorInterval = setInterval(() => {
      const salesFloorIframe = document.getElementById('sf-widget-cookie');

      if (salesFloorIframe) {
        if (salesFloorIframe.style.overflow === 'hidden') {
          salesFloorIframe.setAttribute('role', 'presentation');
        } else {
          salesFloorIframe.setAttribute('title', 'Connect with a Beauty Specialist');
        }

        clearInterval(checkSalesfloorInterval);
      }
    }, 1000);

    return () => {
      clearInterval(checkSalesfloorInterval);
    };
  }, []);

  const isPharmaprix = isPharmaprixBanner();

  const url = `//widgets.services.${isPharmaprix ? banners.PHARMAPRIX : banners.SHOPPERS}.ca`;
  const retailer = isPharmaprix ? 'pharmaprix' : 'shoppers';
  const cacheBustKey = useMemo(() => {
    let key = Cookies.get('cacheBustKey');

    if (!key) {
      const expires = new Date(Date.now() + 288e5);

      key = Date.now().toString();
      Cookies.set('cacheBustKey', key, { expires, path: '/' });
    }

    return key;
  }, []);

  // Note: the way NextJS script works is it injects the script into the body, AND THE SCRIPT PERSISTS AFTER THE
  // COMPONENT UNMOUNTS, but it will not be re-injected on subsequent mounts
  return (
    <Script
      data-testid="widget-script"
      src={`${url}/js/widget.${retailer}.${cacheBustKey}.min.js`}
      strategy="lazyOnload"
    />
  );
};

// Wrap salesfloor around an error boundary instead of try-catch
export class SalesfloorWidget extends React.Component {
  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('WIDGET - LOADER EXCEPTION', error.toString(), errorInfo);
  }

  public render() {
    return <SalesfloorWidgetComponent />;
  }
}

export default SalesfloorWidget;
